<template>
  <Loader :value="contractPollPromise">
    <Card
      tag="form"
      @submit.prevent="submit"
      class="shop-dashboard-widget-signing"
      :class="{ 'shop-dashboard-widget-signing--active': contract.id }"
    >
      <template #header>
        <FontAwesomeIcon :icon="['fal', 'file-contract']" size="lg" />
        <div>
          <h2>{{ $t('shopSigning') }}</h2>
          <Status :value="contract.status" :data-value="contract.status" />
        </div>
      </template>
      <template #default>
        <Paywall :value="[isEnabled]" :items="[true]">
          <template #default>
            <Expiry
              v-if="$glob(contract.status, 'draft ready unsigned')"
              v-model="value"
              :validator="validator"
              :labels="labels"
            />

            <Applicant v-model="value" :disabled="$glob(contract.status, 'signed accepted')">
              <template #below>
                <DeliveryLog
                  v-if="contract.deliveryLogs"
                  theme="pill"
                  class="mt-1 self-start"
                  :resource="$t('invite')"
                  :value="deliveryLogUnsigned"
                />
              </template>
            </Applicant>

            <FileComponent
              v-if="$glob(contract.status, 'draft ready')"
              v-model="value.template.attachments"
              :label="`${$t('shopContractAttachment')}`"
              accept=".pdf"
              :upload-options="{ acl: 'private' }"
              multiple
            >
              <template #below>
                <div class="text--small fade">{{ $t('attachmentsSigningUploadWarning') }}</div>
              </template>
            </FileComponent>
          </template>
          <template #content>
            <Alert v-if="!hasSigningAuthority" theme="warning" style="max-width: 500px">
              <div>{{ $t('shopSigningAuthorityMissing') }}</div>
              <ButtonComponent
                class="mt-1"
                tag="router-link"
                :label="`${$t('edit')} ${$tc('role', 2).toLowerCase()}`"
                :icon="['fal', 'pen']"
                :to="{
                  name: 'project-edit',
                  params: { id: $path('project.id', item), step: 'roles' },
                  query: { tabStep: 'shop' },
                }"
              />
            </Alert>
            <Alert v-if="contract.id && !hasContractTemplate">
              <div class="mb-2">{{ $t('noShopContractTemplate') }}</div>
              <ButtonComponent
                tag="router-link"
                :to="{ name: 'settings', params: { step: 'accountsettings' } }"
                :label="$t('gotoItem', [$t('accountSettings').toLowerCase()])"
                icon="fa-pro-light:cog"
              />
            </Alert>
            <Alert v-if="!contract.id" style="max-width: 405px">
              {{ $t('shopContractNotSold') }}
            </Alert>
          </template>
        </Paywall>
      </template>
      <template #footer>
        <template v-if="isEnabled">
          <Alert v-if="stepValidationMessage" theme="warning">{{ stepValidationMessage }}</Alert>
          <ButtonComponent
            v-if="$glob(contract.status, '!signed !accepted')"
            type="submit"
            :label="$t('save')"
            :promise="promises.mutate"
            :icon="['fal', 'arrow-to-right']"
            theme="secondary"
            :disabled="isDisabled"
          />
          <ButtonComponent
            v-if="$glob(contract.status, 'signed accepted')"
            tag="router-link"
            :label="$t('gotoItem', [$t('shopContract').toLowerCase()])"
            :icon="['fal', 'file-contract']"
            :to="{ name: 'contract', params: { id: contract.id } }"
            theme="success"
            :disabled="isDisabled"
          />
          <template v-if="contract.status === 'draft'">
            <ButtonComponent
              :label="$t('startShopContract')"
              :promise="statusPromise"
              confirm
              :icon="['fal', 'shopping-cart']"
              theme="success"
              @click="updateStatus('unsigned')"
              :disabled="!unsignedEnabled || isDisabled"
            />
          </template>
        </template>
      </template>
      <template #root>
        <Paywall
          :value="addons"
          items="project:shop-purchase"
          :action="addon"
          :options="{ units }"
          style="position: static"
        />
      </template>
    </Card>
  </Loader>
</template>

<script>
import { mapi18n } from '@/i18n'
import Validator from '@/mixins/validator-v2'
import { FileComponent } from '@kvass/file'
import { mapState, mapActions } from 'vuex'
import { Run } from '@/utils'
import Model from '@/models/ShopContract/base'

import { InputComponent } from 'vue-elder-input'
import { Paywall } from '@/components/Upsell'
import Pricing from '@/components/Pricing'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Expiry from './Expiry'
import Card from '@/components/Card'
import Status from './Status'
import Applicant, { RulesExtension } from './Applicant'
import { rules as ApplicantRules, labels as ApplicantLabels } from '@/components/Contract/Applicant'
import DeliveryLog from '@/components/DeliveryLog'

export default {
  mixins: [
    Validator({
      rules: {
        ...ApplicantRules,
        ...RulesExtension,
      },
      labels: ApplicantLabels,
      data: 'value.data',
    }),
  ],
  watch: {
    contract: {
      handler(val) {
        this.value = Model(val)
      },
      immediate: true,
    },
  },
  data() {
    return {
      value: Model(),
      contractPollPromise: null,
      statusPromise: null,
    }
  },
  computed: {
    ...mapi18n('labels', {
      expiresAt: 'deadline',
      signableFrom: 'signableFrom',
    }),
    ...mapState('Contract', ['promises']),
    ...mapState('Shop', {
      item: state => state.item,
      project: state => state.item.project,
      addons(state) {
        return this.$path('item.project.metadata.addons', state) || []
      },
      hasSigningAuthority(state) {
        return this.$path('item.project.roles.shop.signingAuthority.length', state)
      },
      contract: state => state.item.contract || {},
      units(state) {
        return this.$path('item.project.metadata.limits.units', state)
      },
    }),
    hasContractTemplate() {
      return Boolean(this.$path('contract.template.document'))
    },
    isEnabled() {
      return this.hasContractTemplate && this.isSold && Boolean(this.hasSigningAuthority)
    },
    deliveryLogUnsigned() {
      let log = this.contract.deliveryLogs.find(l => l.tag === `contract:${this.contract.id}:unsigned`)
      if (!log && this.contract.status === 'unsigned') return { events: [{ event: 'processing', date: new Date() }] }
      return log
    },
    buyers() {
      let value = []

      if (this.$path('contract.data.applicant.name')) value.push(this.contract.data.applicant)
      if (this.$path('contract.data.hasCoApplicant') && this.$path('contract.data.coApplicant.name'))
        value.push(this.contract.data.coApplicant)

      return value
    },
    isSold() {
      return ['sold', 'rented'].includes(this.$path('item.reference.status'))
    },
    isSignable() {
      return [this.value.data.signableFrom, ...(this.value.data.partials || []).map(p => p.signableFrom)].some(Boolean)
    },
    unsignedEnabled() {
      return Boolean(this.buyers.length)
    },
    stepValidationMessage() {
      if (!this.unsignedEnabled && this.contract.status === 'draft') return this.$t('shopContractStartRequirements')
      if (this.contract.status === 'unsigned' && !this.isSignable) return this.$t('shopContractRememberSignableFrom')
      return ''
    },
    isDisabled() {
      if (!this.validator.passes) return true
      if (!this.hasSigningAuthority) return true
      if (['accepted', 'signed'].includes(this.contract.status)) return false
      return false
    },
  },
  methods: {
    ...mapActions('Contract', {
      submit(dispatch) {
        return dispatch('mutate', [this.value, { mutation: 'ShopContractUpdate', strategy: 'none' }]).then(() => {
          this.$emit('refetch')
        })
      },
      getContract(dispatch) {
        return dispatch('fetch', [
          { id: this.item.id },
          { query: 'ShopContract', setPromise: false, strategy: 'none' },
        ]).then(data => data.contract)
      },
      updateStatus(dispatch, status) {
        this.statusPromise = dispatch('mutate', [
          { id: this.value.id, status },
          { mutation: 'ShopContractUpdate', setPromise: false, strategy: 'none' },
        ]).then(() => {
          this.contract.status = status
          this.$emit('refetch')
        })
      },
    }),
    pollContract() {
      this.contractPollPromise = Run(
        async () => {
          let contract = await this.getContract()
          if (!contract) throw new Error('Could not find contract')
          this.item.contract = contract
        },
        {
          retryCount: 5,
          retryTimeout: 2000,
        },
      )
    },
    addon(addon) {
      this.addonPromise = this.$store
        .dispatch('Project/addon', {
          addon,
          state: true,
          project: this.project.id,
        })
        .then(() => this.addons.push(addon))
        .then(() => {
          if (!this.isSold) return
          this.pollContract()
        })
    },
  },
  components: {
    Card,
    InputComponent,
    Pricing,
    Paywall,
    Applicant,
    FileComponent,
    Status,
    Alert,
    Expiry,
    DeliveryLog,
  },
}
</script>

<style lang="scss">
.shop-dashboard-widget-signing {
  position: relative;

  &--active {
    & > .card__footer {
      display: block !important;
    }
  }

  & > .card__header {
    flex-direction: row;
    justify-content: flex-start;

    & > svg {
      margin-right: 1.5rem;
    }

    .status {
      margin-top: 0.1rem;
    }

    h2 {
      margin: 0;
    }
  }

  & > .card__content {
    .paywall {
      & > * + * {
        margin-top: 1rem;
      }
    }
  }

  & > .card__footer {
    display: none;

    .card__footer-content {
      justify-content: space-between;
      align-items: flex-start;

      .alert {
        width: 100%;
        white-space: break-spaces;
      }
    }
  }

  & > .upsell-paywall {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    .pricing-card {
      margin: 2rem;
    }
  }
}
</style>
